import React, { useState } from 'react';
import { Modal, Button } from 'react-bootstrap';

export interface IAssetHistoryItem {
    date: string;
    service: string;
    status: string;
    notes: string;
    type: string;
}

interface AssetHistoryCardProps {
    assetHistory: IAssetHistoryItem[];
}

const AssetHistoryCard: React.FC<AssetHistoryCardProps> = ({
    assetHistory
}) => {
    const [notesModalVisible, setNotesModalVisible] = useState(false);
    const [selectedItem, setSelectedItem] = useState<IAssetHistoryItem | null>(
        null
    );

    const formatDate = (dateString: string): string => {
        const date = new Date(dateString);
        const day = date.getDate();
        const month = date.toLocaleDateString('en-US', { month: 'short' });
        const year = date
            .getFullYear()
            .toString()
            .slice(-2);
        const time = date.toLocaleTimeString('en-US', {
            hour: 'numeric',
            minute: '2-digit',
            hour12: true
        });
        return `${day}-${month}-${year} ${time}`;
    };

    const formatDateOnly = (dateString: string): string => {
        const date = new Date(dateString);
        const day = date.getDate();
        const month = date.toLocaleDateString('en-US', { month: 'short' });
        const year = date
            .getFullYear()
            .toString()
            .slice(-2);
        return `${day}-${month}-${year}`;
    };

    const getStatusClassName = (status: string): string => {
        const upperStatus = status.toUpperCase();
        if (upperStatus === 'PASS') return 'asset-history-status--pass';
        if (upperStatus === 'REPAIRED') return 'asset-history-status--repaired';
        if (upperStatus === 'FAIL') return 'asset-history-status--fail';
        if (upperStatus === 'CANNOT COMPLETE')
            return 'asset-history-status--cannot-complete';
        return 'asset-history-status--default';
    };

    const handleViewNotes = (item: IAssetHistoryItem) => {
        setSelectedItem(item);
        setNotesModalVisible(true);
    };

    const handleCloseModal = () => {
        setNotesModalVisible(false);
        setSelectedItem(null);
    };

    if (!assetHistory || assetHistory.length === 0) {
        return null;
    }

    return (
        <>
            <div className="asset-history-card">
                <div className="asset-history-card-header">
                    <h3>Asset History</h3>
                </div>
                <div className="asset-history-table-container">
                    <table className="asset-history-table">
                        <thead>
                            <tr>
                                <th>Date</th>
                                <th>Service</th>
                                <th>Status</th>
                                <th>Notes</th>
                            </tr>
                        </thead>
                        <tbody>
                            {assetHistory.map((item, index) => (
                                <tr key={index}>
                                    <td>{formatDateOnly(item.date)}</td>
                                    <td>{item.service}</td>
                                    <td>
                                        <span
                                            className={`asset-history-status ${getStatusClassName(
                                                item.status
                                            )}`}
                                        >
                                            {item.status.toUpperCase()}
                                        </span>
                                    </td>
                                    <td>
                                        {item.notes &&
                                            item.notes.trim() !== '' && (
                                                <button
                                                    className="asset-history-view-notes-btn"
                                                    onClick={() =>
                                                        handleViewNotes(item)
                                                    }
                                                >
                                                    View Notes
                                                </button>
                                            )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>

            <Modal
                show={notesModalVisible}
                onHide={handleCloseModal}
                className="asset-history-notes-modal"
            >
                <Modal.Header>
                    <Modal.Title>NOTES</Modal.Title>
                    <button
                        type="button"
                        className="close"
                        onClick={handleCloseModal}
                        aria-label="Close"
                    >
                        <span aria-hidden="true">&times;</span>
                    </button>
                </Modal.Header>
                <Modal.Body>
                    {selectedItem && (
                        <>
                            <div className="asset-history-notes-status-line">
                                <span className="notes-label">
                                    {selectedItem.service} &ndash;{' '}
                                </span>
                                <span
                                    className={`notes-status notes-status--${selectedItem.status.toLowerCase()}`}
                                >
                                    {selectedItem.status.toUpperCase()}
                                </span>
                            </div>
                            <div className="asset-history-notes-timestamp">
                                {formatDate(selectedItem.date)}
                            </div>
                            <div className="asset-history-notes-label">
                                Notes
                            </div>
                            <div
                                className="asset-history-notes-content"
                                dangerouslySetInnerHTML={{
                                    __html: selectedItem.notes
                                }}
                            />
                        </>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button
                        className="asset-history-notes-close-btn"
                        onClick={handleCloseModal}
                    >
                        Close
                    </Button>
                </Modal.Footer>
            </Modal>
        </>
    );
};

export default AssetHistoryCard;
